import {Card, Segment} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useState} from 'react';

import {GetDirectSaleResponse, GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FullyReturnedItemsSegment} from './FullyReturnedItemsSegment';
import {RequestDetailSegment} from './RequestDetailSegment';
import {WorkItemsSegment} from './WorkItemsSegment';

enum Segments {
  WORK_ITEMS = 'workItems',
  REQUEST_DETAIL = 'requestDetail',
  FULLY_RETURNED = 'fullyReturned',
}

interface RequestTabProps extends TestIdProps {
  directSale: GetDirectSaleResponse;
  isDirectSaleLoading: boolean;
  isEditingDisabled: boolean;
  availableWarehouses: GetWarehousesResponse;
  onMaterialReturnModalOpen: VoidFunction;
}

export function RequestTab(props: RequestTabProps) {
  const [activeSegment, setActiveSegment] = useState<Segments>(Segments.WORK_ITEMS);

  const segments = [
    {
      value: Segments.WORK_ITEMS,
      label: i18n.t('entity.warehouse.labels.workItems', {count: props.directSale.itemsCount ?? 0}),
      content: (
        <WorkItemsSegment
          directSale={props.directSale}
          isEditingDisabled={props.isEditingDisabled}
          availableWarehouses={props.availableWarehouses}
          onMaterialReturnModalOpen={props.onMaterialReturnModalOpen}
          data-testid={suffixTestId('segments.workItems', props)}
        />
      ),
    },
    {
      value: Segments.REQUEST_DETAIL,
      label: i18n.t('entity.warehouse.labels.requestDetail'),
      content: (
        <RequestDetailSegment
          directSale={props.directSale}
          isDirectSaleLoading={props.isDirectSaleLoading}
          isEditingDisabled={props.isEditingDisabled}
          data-testid={suffixTestId('segments.requestDetail', props)}
        />
      ),
    },
    {
      value: Segments.FULLY_RETURNED,
      label: i18n.t('entity.warehouse.labels.fullyReturned', {
        count: props.directSale.itemsFullyReturnedCount ?? 0,
      }),
      content: (
        <FullyReturnedItemsSegment data-testid={suffixTestId('segments.fullyReturned', props)} />
      ),
    },
  ];

  const segmentContent = segments.find((segment) => segment.value === activeSegment)?.content;

  return (
    <Card isFullHeight={activeSegment === Segments.WORK_ITEMS}>
      <VStack height="100%">
        <HStack>
          <Box>
            <Segment<Segments>
              value={activeSegment}
              onChange={setActiveSegment}
              options={segments}
              minWidth={28}
            />
          </Box>
        </HStack>

        <Box height="100%">{segmentContent}</Box>
      </VStack>
    </Card>
  );
}
